<app-dialog>
  <app-dialog-title>
    <language key="CsvExportDialogTitle" defaultValue="Export As CSV"></language>
  </app-dialog-title>
  <div mat-dialog-content>
    <!-- progress spinner -->
    @if (started) {
      <div>
        @if (loading) {
          <div class="d-flex flex-column align-items-center flex-">
            <mat-progress-spinner mode="indeterminate" class="mb-3"></mat-progress-spinner>
            @if (displayedLoadedCount === displayedTotalCount) {
              <p>
                <language
                  key="CsvExportLoadingReferences"
                  defaultValue="Loading References …"
                ></language>
              </p>
            }
            @if (displayedTotalCount != 0) {
              <p>
                {{ formatNumber(displayedLoadedCount) | async }}/{{
                  formatNumber(displayedTotalCount) | async
                }}
              </p>
            }
          </div>
        }
        @if (!loading) {
          <p>
            <language
              key="CsvExportReady"
              defaultValue="Download finished. Ready to export."
            ></language>
          </p>
        }
      </div>
    }

    <!-- settings -->
    @if (!started) {
      <div>
        <div class="mb-3">
          <mat-radio-group>
            <mat-radio-button [checked]="loadAll" (click)="onLoadAll(true)" value="1">
              <language key="AllCSVExport" defaultValue="All"></language>
            </mat-radio-button>
            <mat-radio-button [checked]="!loadAll" (click)="onLoadAll(false)" value="2">
              <language key="DefineRangeCSVExport" defaultValue="Define Range"></language>
            </mat-radio-button>
          </mat-radio-group>
        </div>
        @if (!loadAll) {
          <form [formGroup]="form">
            <mat-form-field class="form-field-numeric">
              <mat-label>
                <language key="StartElementCSVExport" defaultValue="Start Element"></language>
              </mat-label>
              <input
                matInput
                formControlName="rangeStart"
                type="number"
                [min]="min"
                [max]="max - 1"
                [value]="min"
                autocomplete="off"
              />
            </mat-form-field>
            :
            <mat-form-field class="form-field-numeric">
              <mat-label>
                <language key="StopElementCSVExport" defaultValue="Stop Element"></language>
              </mat-label>
              <input
                matInput
                formControlName="rangeEnd"
                type="number"
                [min]="min + 1"
                [max]="max"
                [value]="max"
                autocomplete="off"
              />
            </mat-form-field>
            @if (form.touched) {
              <div>
                @if (form.errors && form.errors.range) {
                  <mat-error>
                    <language
                      key="CSVErrorRange"
                      defaultValue="The start element needs to be smaller than the stop element."
                    ></language>
                  </mat-error>
                }
              </div>
            }
            @if (form.get('rangeStart').touched) {
              <div>
                @if (form.get('rangeStart').hasError('required')) {
                  <mat-error>
                    <language
                      key="ControlErrorRequired"
                      defaultValue="This field is required."
                    ></language>
                  </mat-error>
                }
                @if (form.get('rangeStart').hasError('min')) {
                  <mat-error>
                    <language
                      key="ControlErrorMin"
                      defaultValue="Please choose a value greater or equal to {0}."
                      [args]="[min]"
                    ></language>
                  </mat-error>
                }
                @if (form.get('rangeStart').hasError('max')) {
                  <mat-error>
                    <language
                      key="ControlErrorMax"
                      defaultValue="Please choose a value lower or equal to {0}."
                      [args]="[max]"
                    ></language>
                  </mat-error>
                }
              </div>
            }
            @if (form.get('rangeEnd').touched) {
              <div>
                @if (form.get('rangeEnd').hasError('required')) {
                  <mat-error>
                    <language
                      key="ControlErrorRequired"
                      defaultValue="This field is required."
                    ></language>
                  </mat-error>
                }
                @if (form.get('rangeEnd').hasError('min')) {
                  <mat-error>
                    <language
                      key="ControlErrorMin"
                      defaultValue="Please choose a value greater or equal to {0}."
                      [args]="[min]"
                    ></language>
                  </mat-error>
                }
                @if (form.get('rangeEnd').hasError('max')) {
                  <mat-error>
                    <language
                      key="ControlErrorMax"
                      defaultValue="Please choose a value lower or equal to {0}."
                      [args]="[max]"
                    ></language>
                  </mat-error>
                }
              </div>
            }
          </form>
        }
        <mat-form-field appearance="fill">
          <mat-label>
            <language key="CsvExportDelimiterLabel" defaultValue="Delimiter"></language>
          </mat-label>
          <mat-select required [(ngModel)]="delimiter">
            <mat-option value="	">
              <language key="CsvExportDelimiterTab" defaultValue="Tab"></language>
            </mat-option>
            <mat-option value=";">
              <language key="CsvExportDelimiterSemicolon" defaultValue="Semicolon"></language>
            </mat-option>
            <mat-option value=",">
              <language key="CsvExportDelimiterComma" defaultValue="Comma"></language>
            </mat-option>
            <mat-option value=" ">
              <language key="CsvExportDelimiterSpace" defaultValue="Space"></language>
            </mat-option>
          </mat-select>
        </mat-form-field>
        @if (referenceAttributes | async; as refAttribs) {
          @if (refAttribs?.length > 0) {
            <div class="attribute-reference-replacement-settings">
              <h4>
                <language
                  key="CsvExportReferenceAttributesTitle"
                  defaultValue="Replace References by the following Attributes"
                ></language>
              </h4>
              @for (da of refAttribs; track da) {
                <div class="row">
                  <div class="col-12 col-md-3 attribute-reference-replacement-source">
                    {{ da.DisplayName }}
                  </div>
                  <div class="col-12 col-md-9 attribute-reference-replacement-target">
                    <mat-form-field class="w-100" appearance="fill">
                      <mat-label>
                        <language
                          key="ResourceSummaryDialogAttributeColumn"
                          defaultValue="Attribute"
                        ></language>
                      </mat-label>
                      <input
                        type="text"
                        matInput
                        [formControl]="getFormControlForReferenceAttribute(da.Name)"
                        [matAutocomplete]="auto"
                        autocomplete="off"
                      />
                      <mat-autocomplete
                        #auto="matAutocomplete"
                        [displayWith]="displayAttributeFilterValue"
                      >
                        @for (
                          atd of getAllFilteredAttributesForReferenceAttribute(da.Name) | async;
                          track atd
                        ) {
                          <mat-option [value]="atd">
                            {{ atd.displayname }}
                          </mat-option>
                        }
                      </mat-autocomplete>
                    </mat-form-field>
                  </div>
                </div>
              }
            </div>
          }
        }
      </div>
    }
  </div>

  <app-dialog-action-button (click)="dialogRef.close()">
    <language key="Cancel" defaultValue="Cancel"></language>
  </app-dialog-action-button>

  @if (!started) {
    <app-dialog-action-button
      [primary]="true"
      (click)="startDownload()"
      [disabled]="form.invalid && !loadAll"
    >
      <language key="StartDownload" defaultValue="Start Download"></language>
    </app-dialog-action-button>
  } @else if (loading) {
    <app-dialog-action-button [primary]="true" [disabled]="true">
      <language key="DatatableCSVExport" defaultValue="Export CSV"></language>
    </app-dialog-action-button>
  } @else {
    <a [href]="objectUrl" [download]="fileName" (click)="submit()" app-dialog-action-button-custom>
      <button type="button" matButton cdkFocusInitial>
        <language key="DatatableCSVExport" defaultValue="Export CSV"></language></button
    ></a>
  }
</app-dialog>
